import React, { useState, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  useReactFlow,
  ReactFlowInstance
} from 'reactflow';
import 'reactflow/dist/style.css';

import { GraphControls } from './GraphControls';
import { GraphAnnotation } from './GraphAnnotation';
import { nodeTypes } from './NodeTypes';
import { edgeTypes } from './EdgeTypes';
import { applyForceDirectedLayout } from './GraphLayout';
import { useGraphContext } from './GraphContext';
import NodeGroup from './NodeGroup';

// Add NodeGroup to nodeTypes outside the component
const baseEnhancedNodeTypes = {
  ...nodeTypes,
  group: NodeGroup,
};

interface ProcessingGraphProps {
  sessionId?: string;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  readOnly?: boolean;
  showControls?: boolean;
  showMinimap?: boolean;
  height?: string;
  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge) => void;
  onNodeGroupClick?: (groupId: string) => void;
  annotations?: any[];
  onAddAnnotation?: (annotation: any) => void;
  onDeleteAnnotation?: (annotationId: string) => void;
}

export const ProcessingGraph: React.FC<ProcessingGraphProps> = ({
  sessionId,
  initialNodes = [],
  initialEdges = [],
  readOnly = false,
  showControls = true,
  showMinimap = true,
  height = '600px',
  onNodeClick,
  onEdgeClick,
  onNodeGroupClick
}) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { fitView } = useReactFlow();
  const { loadGraphData, saveGraphLayout, expandNode } = useGraphContext();
  
  // New state for filtering
  const [nodeTypeFilter, setNodeTypeFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Memoize nodeTypes to ensure it doesn't change on re-renders
  const enhancedNodeTypes = useMemo(() => baseEnhancedNodeTypes, []);
  
  // Filter nodes based on type and search term
  const filteredNodes = useMemo(() => {
    return nodes.filter(node => {
      // Filter by node type if filters are active
      if (nodeTypeFilter.length > 0 && !nodeTypeFilter.includes(node.type || '')) {
        return false;
      }
      
      // Filter by search term if present
      if (searchTerm && node.data?.label) {
        return node.data.label.toLowerCase().includes(searchTerm.toLowerCase());
      }
      
      return true;
    });
  }, [nodes, nodeTypeFilter, searchTerm]);
  
  // Group nodes by type for the filter UI
  const nodeTypeCount = useMemo(() => {
    const counts: Record<string, number> = {};
    nodes.forEach(node => {
      const type = node.type || 'default';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }, [nodes]);

  // Load graph data if sessionId is provided
  React.useEffect(() => {
    if (sessionId) {
      loadGraphData(sessionId).then(({ nodes, edges }) => {
        setNodes(nodes);
        setEdges(edges);
        setTimeout(() => fitView({ padding: 0.2 }), 100);
      });
    }
  }, [sessionId, loadGraphData, setNodes, setEdges, fitView]);

  // Handle connections between nodes
  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge({ ...params, type: 'weighted' }, eds)),
    [setEdges]
  );

  // Initialize ReactFlow instance
  const onInit = useCallback(
    (instance: ReactFlowInstance) => {
      setReactFlowInstance(instance);
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    },
    [fitView]
  );

  // Apply force-directed layout
  const applyForceLayout = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = applyForceDirectedLayout(nodes, edges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setTimeout(() => fitView({ padding: 0.2 }), 100);
  }, [nodes, edges, setNodes, setEdges, fitView]);

  // Save current layout
  const saveLayout = useCallback(() => {
    if (sessionId && reactFlowInstance) {
      const flowNodes = reactFlowInstance.getNodes();
      const flowEdges = reactFlowInstance.getEdges();
      saveGraphLayout(sessionId, flowNodes, flowEdges);
    }
  }, [sessionId, reactFlowInstance, saveGraphLayout]);
  
  // Create a node group
  const createNodeGroup = useCallback(() => {
    const selectedNodes = nodes.filter(node => node.selected);
    
    if (selectedNodes.length < 2) {
      alert('Please select at least 2 nodes to group');
      return;
    }
    
    // Calculate group position and dimensions
    const minX = Math.min(...selectedNodes.map(node => node.position.x));
    const minY = Math.min(...selectedNodes.map(node => node.position.y));
    const maxX = Math.max(...selectedNodes.map(node => node.position.x + (node.width || 0)));
    const maxY = Math.max(...selectedNodes.map(node => node.position.y + (node.height || 0)));
    
    const groupId = `group-${Date.now()}`;
    const groupNode: Node = {
      id: groupId,
      type: 'group',
      position: { x: minX - 20, y: minY - 40 },
      style: { 
        width: maxX - minX + 40, 
        height: maxY - minY + 80 
      },
      data: { 
        label: 'New Group',
        nodeIds: selectedNodes.map(node => node.id),
        collapsed: false
      }
    };
    
    // Update the nodes to be part of this group
    const updatedNodes = nodes.map(node => {
      if (selectedNodes.some(selectedNode => selectedNode.id === node.id)) {
        return {
          ...node,
          parentNode: groupId,
          extent: 'parent',
          selected: false
        };
      }
      return node;
    });
    
    setNodes([...updatedNodes, groupNode]);
  }, [nodes, setNodes]);
  
  // Handle node expansion
  const handleExpandNode = useCallback((nodeId: string, nodeType: string) => {
    expandNode(nodeId, nodeType).then(({ nodes: newNodes, edges: newEdges }) => {
      setNodes(prevNodes => [...prevNodes, ...newNodes]);
      setEdges(prevEdges => [...prevEdges, ...newEdges]);
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    });
  }, [expandNode, setNodes, setEdges, fitView]);

  // Handle adding an annotation
  const handleAddAnnotation = useCallback((annotation: any) => {
    if (onAddAnnotation) {
      onAddAnnotation(annotation);
      
      // Create annotation node
      const annotationNode: Node = {
        id: annotation.id,
        type: 'annotation',
        position: annotation.position,
        data: {
          text: annotation.text,
          color: annotation.color,
          onDelete: handleDeleteAnnotation
        }
      };
      
      setNodes(prevNodes => [...prevNodes, annotationNode]);
    }
  }, [onAddAnnotation, setNodes]);
  
  // Handle deleting an annotation
  const handleDeleteAnnotation = useCallback((annotationId: string) => {
    if (onDeleteAnnotation) {
      onDeleteAnnotation(annotationId);
      
      // Remove annotation node
      setNodes(prevNodes => 
        prevNodes.filter(node => node.id !== annotationId)
      );
    }
  }, [onDeleteAnnotation, setNodes]);
  
  // Add annotation nodes when annotations prop changes
  useEffect(() => {
    if (annotations && annotations.length > 0) {
      // Create annotation nodes
      const annotationNodes = annotations.map(annotation => ({
        id: annotation.id,
        type: 'annotation',
        position: annotation.position,
        data: {
          text: annotation.text,
          color: annotation.color,
          onDelete: handleDeleteAnnotation
        }
      }));
      
      // Add annotation nodes to the graph
      setNodes(prevNodes => {
        // Remove existing annotation nodes
        const filteredNodes = prevNodes.filter(node => node.type !== 'annotation');
        return [...filteredNodes, ...annotationNodes];
      });
    }
  }, [annotations, handleDeleteAnnotation, setNodes]);

  return (
    <div className="processing-graph-container" style={{ height }}>
      <ReactFlowProvider>
        <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ height: '100%' }}>
          {/* Add filter controls */}
          {showControls && (
            <div className="absolute top-4 left-4 z-10 bg-white p-3 rounded-lg shadow-lg max-w-xs">
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Search nodes..."
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="text-sm font-medium mb-1">Filter by type:</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(nodeTypeCount).map(([type, count]) => (
                  <button
                    key={type}
                    className={`px-2 py-1 text-xs rounded-full ${
                      nodeTypeFilter.includes(type)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                    onClick={() => {
                      setNodeTypeFilter(prev => 
                        prev.includes(type)
                          ? prev.filter(t => t !== type)
                          : [...prev, type]
                      );
                    }}
                  >
                    {type} ({count})
                  </button>
                ))}
                {Object.keys(nodeTypeCount).length > 0 && (
                  <button
                    className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700"
                    onClick={() => setNodeTypeFilter([])}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Annotation controls */}
          {showControls && !readOnly && onAddAnnotation && (
            <div className="absolute top-4 right-4 z-10">
              <GraphAnnotation
                sessionId={sessionId || ''}
                onAddAnnotation={handleAddAnnotation}
                onDeleteAnnotation={handleDeleteAnnotation}
              />
            </div>
          )}
          
          <ReactFlow
            nodes={filteredNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={onInit}
            nodeTypes={enhancedNodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-right"
            minZoom={0.1}
            maxZoom={4}
            onNodeClick={(_, node) => onNodeClick && onNodeClick(node)}
            onEdgeClick={(_, edge) => onEdgeClick && onEdgeClick(edge)}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={!readOnly}
          >
            <Background color="#aaa" gap={16} />
            {showControls && <Controls />}
            {showMinimap && <MiniMap />}
          </ReactFlow>
        </div>
        
        {!readOnly && (
          <GraphControls
            onApplyForceLayout={applyForceLayout}
            onSaveLayout={saveLayout}
            onFitView={() => fitView({ padding: 0.2 })}
            onCreateGroup={createNodeGroup}
            onExpandNode={handleExpandNode}
          />
        )}
      </ReactFlowProvider>
    </div>
  );
};
